import { ProjectBase } from '../components/ProjectBase';
import { ProjectInterface, } from '../pages/Projects';
import { ProjectType } from "src/components/ProjectType";

import cover from "../assets/img/run01-img2.png";
import video from "../assets/vid/r01-video.mp4";
import { Col } from 'react-bootstrap';
import Dropdown from 'src/components/Dropdown';
import { CopyBlock, hybrid } from 'react-code-blocks';
import { renderTextBlock } from 'src/components/RenderTextBlocks';
import Video from 'src/components/Video';


export const projectDetails: ProjectInterface = {
    url: "run01",
    title: "Run01: 'til Mayhem",
    summary: "Run01 is a side-scrolling endless runner game for mobile.",
    description: `Run01 is a side-scrolling endless runner game for mobile where you're helping a mutant experiment with its task.

    As you progress, the game gets more challenging with obstacles appearing more frequently and at higher speeds. You'll need quick reflexes and strategic thinking to navigate through the levels.

    My inspiration for the game was [link=Chrome's Dino Game|https://en.wikipedia.org/wiki/Dinosaur_Game], but with a twist on having two runnable planes and being able to teleport between them.

    Collect the dark stones and keep running, until mayhem.`,
    year: "2022",
    tags: ["Endless Runner", "Racing", "Mobile", "Unity"],
    imageSrc: cover,
    hasVideo: true,
    startVideoTime: 20,
    videoSrc: video,
    isCoverType: true,
    platforms: ["Mobile"],
    projectType: ProjectType.PERSONAL,
    haveTags: true,
    roles: ["Game Programmer"],
    technologies: ["Unity", "C#", "Mobile"],
    collaborators: ["Lucas Coutinho"],
    highlightColor: "#60151F",
    descriptionTitle: "Run, teleport, panic, repeat",
    linkData:
        [
            { type: 'github', url: "https://github.com/lucasctnh/run01-unity-game" },
            { type: 'text', url: "https://github.com/lucasctnh/run01-unity-game/releases/" },
        ],
    relatedProjects: ["StreetRacing", "WarehouseInvasion", "CheckMore"],
};

export function Run01() {
    return (
        <div>
            <ProjectBase project={projectDetails}
                useCarousel={false}
                titleSize={3.2}
                leftContent={
                    <div className="a-left-no-bg">
                        {/* <img src={gif} alt="Gameplay" className='a-image' /> */}
                        <Video src={video} />
                    </div>
                }
                rightContent={
                    null
                }>

                <div className="a-body">

                    <div className="a-body-block">
                        <Col size={12} md={12} className="a-body-left" style={{ marginTop: "0" }}>

                            <div className="a-subtitle">
                                Tech in-depth
                            </div>

                            <div className="a-body-description">
                                {renderTextBlock(`Well, the game was made using Unity and C#. And, although I'm still very newbie to software architecture and design patterns I wanted to practice so I first structured the project following a MVC-ish design structure, in particular for having the data separated.

                                Since my goal was to decouple as much as I could without sacrificing understandability (since I was going to publish the game as open source), I mostly written event-driven systems, but — seeing now that is finished — I also mixed in some singletons here and there which are totally against my goal and probably made the code worse to understand, specially in some systems that had characteristics of both (in these, I should've just committed to the singletons, for simplicity's sake, or tried to make them event-driven single instances).

                                Looking back at everything now I realized that since I mostly used event-driven architecture I could have created an Event Manager for subscribing events where they're needed without making it manually — as I did. For a game of this size probably wouldn't make a significant difference but this is something I'll definitely keep in mind for the next projects.`)}

                            </div>

                            <Dropdown label="Endless-runner gameplay" maxHeight='2000px'>
                                <div className="a-body-description">
                                    {renderTextBlock(`What I did was to keep the player still while everything else moves towards him/her. The player has a base speed (actually the speed of objects) that is increased any amount by time — both configurable, the to-increase quantity and the to-repeat time.

                                    Besides, to truly make the impression of it being endless the environment assets should be seamlessly doubled. That way when the object is too far it's simply translated to a nearer position hence, because it's doubled, the infinity illusion.`)}
                                </div>

                                <Dropdown label="CODE SNIPPET - Infinite move left" maxHeight='1000px' isCodeBlock={true}>

                                    <div className="a-body-description">
                                        Below is the snippet that handles this. The <code>_repeatWidth</code> takes in account that the object <b>x</b> position is 0 (and also that it has a <i>BoxCollider</i> attached to it).
                                    </div>

                                    <div className='a-code-block'>
                                        <CopyBlock
                                            text={`
public class MoveLeftAndRepeat : MonoBehaviour {
[Range(0f, 1f)]
[SerializeField] private float _customSpeed = 1f;

private Vector3 _startPos;
private float _repeatWidth;

private void Start() {
    _startPos = transform.position;
    _repeatWidth = GetComponent<BoxCollider>().size.x / 2 - GetComponent<BoxCollider>().center.x;
}

private void FixedUpdate() {
    if (!GameManager.Instance.isGameRunning)
        return;

    transform.Translate(Vector3.left * GameManager.Instance.playerSpeed * _customSpeed * Time.fixedDeltaTime, Space.World);

    if (transform.position.x < _startPos.x - _repeatWidth)
        transform.position = _startPos;
    }
}
                                            `}
                                            language="csharp"
                                            showLineNumbers={true}
                                            theme={hybrid}
                                        />
                                    </div>
                                </Dropdown>
                            </Dropdown>

                            <Dropdown label="Spawner logic (for collectables and obstacles)" maxHeight='2000px'>

                                <div className="a-body-description">
                                    {renderTextBlock(`For this I created a base class called *Hitable* which handled the common methods like moving towards the player, defining the spawn rate, etc. Them, the inherited classes *Collectable* and *Obstacle* implemented specifics functions like what to do when collide with something and generating a random position for every spawn.

                                    The spawner, or better, the spawners (one for each inherited class) receives an array of *Hitable* prefabs and creates an object pool for each element. Each time there's a call for a spawn they check if it should spawn (according to the spawn rate of the object) or try another.`)}

                                </div>

                                <Dropdown label="CODE SNIPPET - Spawn Manager Core" maxHeight='1000px' isCodeBlock={true}>

                                    <div className="a-body-description">
                                        While the game was running (not in menu) the spawner would keep spawning at a configurable rate.
                                    </div>

                                    <div className='a-code-block'>
                                        <CopyBlock
                                            text={`
private void Update() {
	if (GameManager.Instance.isGameRunning)
		GameManager.CallRepeating(SpawnHitable, ref _timer, repeatRate);
}
                                            `}
                                            language="csharp"
                                            showLineNumbers={true}
                                            theme={hybrid}
                                        />
                                    </div>

                                    <div className="a-body-description">
                                        Below is the spawn method, it selects a random pool and sets the kill behaviour (just a release back to the pool), them it checks whether that hitable is supposed to actually spawn or not, and if not the spawner tries another.
                                    </div>

                                    <div className='a-code-block'>
                                        <CopyBlock
                                            text={`
private void SpawnHitable() {
  while (_isSelectingPool) {
		ObjectPool<Hitable> pool = GetRandomPool();
		Hitable hitable = pool.Get();
		hitable.GetComponent<Hitable>().SetKill(hitable => Kill(pool, hitable));

		// ...

		if (!CheckShouldSpawn(hitable))
			pool.Release(hitable);
	}

	_isSelectingPool = true;
}

private bool CheckShouldSpawn(Hitable hitable) {
	if (hitable.SpawnRate <= 0f)
		return false;
	else if (Random.Range(0f, 1f) <= hitable.SpawnRate) {
		_isSelectingPool = false;
		return true;
	}
	else
		return false;
}
                                            `}
                                            language="csharp"
                                            showLineNumbers={true}
                                            theme={hybrid}
                                        />
                                    </div>

                                </Dropdown>
                            </Dropdown>

                            <Dropdown label="User Interface implementation" maxHeight='2000px'>
                                <div className="a-body-description">
                                    I've used only one scene because of it's complexity — or lack of — so the UI was pretty simple to implement. I created an UI Manager that was an event-driven single instance and whenever another system asked for an element from the UI the manager handled it by properly coroutining and waiting animations before showing or hiding the proper element.
                                </div>
                            </Dropdown>

                            <Dropdown label="Skins system" maxHeight='2000px'>
                                <div className="a-body-description">
                                    {renderTextBlock(`For the skins system I've created a *Skin* model that holds an array of materials (needed because the model I used has multiple materials) and a *Condition*, which in turn is a model that holds all the information for unlocking a skin.

                                    The system class itself holds a list of skins and their configurable conditions, simply accessing the *Condition* model of a skin whenever needed.`)}
                                </div>

                                <Dropdown label="CODE SNIPPET - Condition Model" maxHeight='1000px' isCodeBlock={true}>
                                    <div className='a-code-block'>
                                        <CopyBlock
                                            text={`
public class Condition {
	[Serializable]
	public struct ConditionByAmount {
		[Tooltip("Wheter the condition is by amount or not")]
		public bool isConditionByAmount;

		[Tooltip("Check for amount of coins, uncheck for amount of score")]
		public bool isAmountCoin;

		[Tooltip("The targeted amount")]
		public int targetAmount;
	}

	public string phrase;
	public bool isUnlocked;

	[Tooltip("Function event that will verify the condition and set the unlock button enabled mode accordingly")]
	public UnityEvent condition;
	public ConditionByAmount conditionByAmount;
}
                                            `}
                                            language="csharp"
                                            showLineNumbers={true}
                                            theme={hybrid}
                                        />
                                    </div>
                                </Dropdown>
                            </Dropdown>

                            <Dropdown label="Audio manager" maxHeight='2000px'>
                                <div className="a-body-description">
                                    Quite simply the audio manager handles a list of <i>AudioSources</i>, each of which I call a <b>track</b>. For every method (except for a few that changes all the list) is given the number of the track and they proceed to perform their action (play sound, stop, etc) on the respective track.
                                </div>
                            </Dropdown>

                        </Col>
                    </div>

                    <div className="a-body-block">
                        <Col size={12} md={12} className="a-body-left" style={{ marginTop: "0" }}>

                            <div className="a-subtitle">
                                Afterthoughts
                            </div>

                            <div className="a-body-description">
                                {renderTextBlock(`This project was quite fun to do. My ultimate goal was a simple game, one that I could actually publish, something I haven't done yet. So, I took the simplest game I could thought of (Chrome's Dinosaur Game) and built on top of that, for mobile — because it's was also something I haven't tried. 😎

                                Well, because of the mistakes I was doing in my projects regarding planning stuff in this one I tried to spent more time in that stage and I think it actually paid off, specially for the coding because I already had a base of the systems I would use and how I'd build them.

                                One thing that I didn't like though was that I had this objective of doing all the assets by myself, you know, so I planned for doing these assets to match the game initial's concept (the player would be a monkey that would run on top of a trunk) but making assets is not my strongest skill so It was taking a lot of time (and also looking pretty bad 😛) which went the opposite of another objective I had — don't take too long to finish.

                                In the end, I decided to use third party assets — I'll probably keep using them until I'm comfortable making my own. Besides, the game concept changed because of that but the only downside was the flexibility for making new skins.

                                Overall I had a great time and learned a lot concerning mostly mobile and optimizations. The game is available as an APK in my [github](https://ww.github.com/lucasctnh), funny story about that: I didn't know that I had to pay to publish in a store, yeah I know should've thought that, looking now it's seems pretty obvious 😅. Anyway, until I'm able to afford that it will stay an APK.`)}
                            </div>
                        </Col>
                    </div>
                </div>

            </ProjectBase >
        </div >
    );
}